<template>
  <div class="row mr-0 ml-0 sessionStyle" @click="makeOrEditBet">
    <Popper
      v-if="Math.round(positionNet) !== 0"
      hover
      arrow
      placement="top"
      :open-delay="100"
      class="position-net"
      :class="positionNet > 0 ? 'position-net-win' : 'position-net-lose'"
    >
      <span class="position-net-badge" @click.stop="handlePositionBadgeClick">
        {{ positionNet > 0 ? '+' : '-' }}{{ formattedPositionNet }}
      </span>
      <template #content>
        {{ positionNetTooltip }}
      </template>
    </Popper>
    <div
      v-if="hasSelfSession(moneylines, userStore.user.id)"
      :class="[`moneyline-session-ownUser`]"
    >
      {{ displayOdds }}
    </div>
    <div
      v-else-if="hasMarketMakerSession(moneylines)"
      :class="`moneyline-session-marketmaker`"
    >
      {{ displayOdds }}
    </div>
    <div v-else :class="[`moneyline-session-filler`, 'text-center']">
      <font-awesome-icon :icon="faPlus" />
    </div>
  </div>
</template>

<script setup>
import { useUserStore } from '@/stores/user';
import { computed, inject } from 'vue';
import { formatOdds, formatOddsRounded } from '@/core/oddsFormatUtils';
import { hasSelfSession, hasMarketMakerSession, addComa } from '@/core/utils';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import Popper from 'vue3-popper';

const userStore = useUserStore();
const openMarketPositionModal = inject('openMarketPositionModal', null);

const props = defineProps({
  moneylines: {
    type: Array,
    default: null,
  },
  league: {
    type: String,
    required: true,
  },
  side: {
    default: '',
    type: String,
  },
  isMobile: {
    type: Boolean,
    default: false,
  },
  // Rounds odds to whole numbers on the homepage cards where decimal odds make the cells too wide
  roundedHomepageOdds: {
    type: Boolean,
    default: false,
  },
  // Net win/loss for the user if this side of the market hits
  positionNet: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['makeBet', 'editOrderModal']);

const moneyline = computed(() => {
  return Array.isArray(props.moneylines)
    ? props.moneylines[0]
    : props.moneylines;
});

const displayOdds = computed(() => {
  const fn = props.roundedHomepageOdds ? formatOddsRounded : formatOdds;
  return fn(moneyline.value.odds, props.league, 'moneyline', false, userStore);
});

const positionNetTooltip = computed(() => {
  const amount = Number(Math.abs(props.positionNet)).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return props.positionNet > 0
    ? `If this side hits, you win $${amount}`
    : `If this side hits, you lose $${amount}`;
});

const formattedPositionNet = computed(() =>
  addComa(Math.abs(props.positionNet)),
);

// MoneyLine renders both the 2-way moneyline and the 1x2 yes/no cells, so the
// market the badge belongs to comes off the session itself.
const marketType = computed(() => props.moneylines?.[0]?.type || 'moneyline');

function makeOrEditBet() {
  const selfSession = hasSelfSession(props.moneylines, userStore.user.id, true);
  if (selfSession) {
    emit('editOrderModal', selfSession);
  } else {
    emit('makeBet', props.moneylines);
  }
}

function handlePositionBadgeClick() {
  if (typeof openMarketPositionModal === 'function') {
    openMarketPositionModal(marketType.value);
  }
}
</script>

<style scoped lang="scss">
@use '@/scss/tokens.scss' as main;

.moneyline-session-marketmaker,
.moneyline-session-ownUser,
.moneyline-session-filler {
  padding: 0.5rem;
  position: relative;
}

.sessionStyle {
  width: fit-content;
  align-items: center;
  justify-content: center;
  overflow: visible !important;
  position: relative;
}

.position-net {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  margin: 0 !important;
  border: none !important;
}

.position-net-badge {
  display: inline-block;
  font-size: 8px;
  line-height: 1;
  font-weight: bold;
  padding: 2px 4px 3px 3px;
  border-bottom-right-radius: 6px;
  cursor: help;
}

.position-net-win .position-net-badge {
  color: main.$green-500;
  background: color-mix(in srgb, var(--green-500) 16%, transparent);
}

.position-net-lose .position-net-badge {
  color: main.$red-500;
  background: color-mix(in srgb, var(--red-500) 16%, transparent);
}

.position-net :deep(.popper) {
  background: main.$white_01 !important;
  color: main.$black-102 !important;
  border: 1px main.$grey-101 solid !important;
  border-radius: 4px !important;
  box-shadow: 1px 1px 5px 1px main.$blue-100 !important;
  padding: 6px 8px !important;
  font-size: main.$font-size-xs !important;
  font-weight: main.$font-weight-normal !important;
  width: max-content !important;
  max-width: 14rem !important;
  text-align: center;
}

.position-net :deep(.popper #arrow::before) {
  background: main.$white_01;
}

.size {
  font-size: 6px;
  margin-top: 2px;
}
</style>
